import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Pencil, Plus, Trash2, UserRoundCog } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Empty, ErrorNotice } from "../../components/Feedback";
import type { WorkContext } from "../../types";
import {
  contractApi, deleteDatabaseUser, inviteContractUser, usersAdminApi,
} from "./api";
import type {
  ContractCatalog, ContractUser, Scope, UserOption, UserPage, PersonalProfile,
} from "./api";

type ModalKind = "assign" | "manage" | "profile" | "delete" | null;
type AssignMode = "existing" | "invite";
const roleNames: Record<string, string> = {
  admin: "Administrador", coordinador: "Coordinador", supervisor: "Supervisor",
  auxiliar: "Auxiliar", lector: "Lector",
};
const emptyProfile: PersonalProfile = {
  nombre: "", apellido_paterno: "", apellido_materno: "", telefono: "", notas: "",
};

interface Props {
  catalog: ContractCatalog;
  context: WorkContext;
  reload: () => Promise<void>;
  refresh: () => Promise<void>;
  currentUserId?: string;
}
export function ContractUsersPanel({ catalog, context, reload, refresh, currentUserId }: Props) {
  const [modal, setModal] = useState<ModalKind>(null);
  const [assignMode, setAssignMode] = useState<AssignMode>("existing");
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [areaId, setAreaId] = useState("");
  const [role, setRole] = useState("supervisor");
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [available, setAvailable] = useState<UserPage>({ usuarios: [], total: 0, pagina: 0, tamano: 50 });
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("");
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [profile, setProfile] = useState<PersonalProfile>(emptyProfile);
  const [profileLoading, setProfileLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!error) return;
    const timeout = window.setTimeout(() => setError(""), 4000);
    return () => window.clearTimeout(timeout);
  }, [error]);
  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(""), 3000);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const activeUser: ContractUser | null = useMemo(() => {
    if (!selected) return null;
    return catalog.usuarios.find(u => u.user_id === selected.user_id)
      ?? { ...selected, asignaciones: [] };
  }, [catalog.usuarios, selected]);

  const canManageArea = (id: string) =>
    catalog.admin || catalog.coordinador_areas.includes(id);
  const assignableAreas = useMemo(
    () => catalog.areas.filter(a => catalog.admin || catalog.coordinador_areas.includes(a.id)),
    [catalog],
  );
  const entries = activeUser?.asignaciones.filter(a => a.area_id === areaId) ?? [];
  const existingEntry = entries[0];
  const isActiveInArea = entries.some(e => e.is_active);
  const canEditSelected = Boolean(
    areaId && canManageArea(areaId) &&
      !activeUser?.asignaciones.some(a => a.rol === "admin") &&
      (!existingEntry || isActiveInArea) &&
      (catalog.admin || (existingEntry && existingEntry.rol !== "coordinador" && isActiveInArea)),
  );

  const readArea = (user: ContractUser | UserOption, id: string) => {
    const assigned = ("asignaciones" in user ? user.asignaciones : [])
      ?.filter(a => a.area_id === id) ?? [];
    setAreaId(id);
    setRole(assigned[0]?.rol ?? "supervisor");
    const active = assigned.filter(a => a.is_active && a.frente_id);
    setScopes(active.map(a => ({ frente_id: a.frente_id, subfrente_id: a.subfrente_id })));
    setError("");
  };

  const openManage = (user: ContractUser | UserOption) => {
    setSelected({ user_id: user.user_id, nombre: user.nombre, correo: user.correo });
    const allowed = assignableAreas.filter(a =>
      catalog.admin || ("asignaciones" in user &&
        user.asignaciones?.some(entry => entry.area_id === a.id)));
    const id = ("asignaciones" in user && user.asignaciones?.find(a =>
      a.area_id && allowed.some(area => area.id === a.area_id))?.area_id) || allowed[0]?.id || "";
    readArea(user, id);
    setModal("manage");
    setError("");
  };
  const close = () => { if (!busy) { setModal(null); setError(""); } };
  const loadAvailable = useCallback(async (nextPage: number, nextFilter: string) => {
    if (!catalog.admin) return;
    setLoadingAvailable(true);
    try {
      const response = await usersAdminApi<UserPage>(context,"listar_disponibles",{
        pagina: nextPage, filtro: nextFilter.trim(),
      });
      setAvailable(response);
      setPage(nextPage);
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setLoadingAvailable(false); }
  }, [catalog.admin,context]);
  useEffect(() => {
    if (modal !== "assign" || assignMode !== "existing") return;
    void loadAvailable(0,"");
  }, [modal,assignMode,loadAvailable]);

  const openAssign = () => {
    setModal("assign");
    setAssignMode("existing");
    setFilter("");
    setPage(0);
    setSelected(null);
    setInviteName("");
    setInviteEmail("");
    setError("");
  };
  const finish = async (success: string) => {
    await reload();
    await refresh();
    setNotice(success);
  };

  const handleInvite = async (event: FormEvent) => {
    event.preventDefault();
    if (!catalog.admin || busy) return;
    setBusy(true); setError("");
    try {
      const invited = await inviteContractUser(context, {
        nombre: inviteName.trim(), email: inviteEmail.trim(),
      });
      await finish("Invitación enviada. Asigna ahora el área, rol y frentes.");
      openManage({
        user_id: invited.user_id, nombre: inviteName.trim(), correo: inviteEmail.trim(),
      });
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };
  const saveAssignment = async () => {
    if (!activeUser || !canEditSelected || busy) return;
    if (role !== "coordinador" && scopes.length === 0) {
      setError("Selecciona al menos un frente o subfrente."); return;
    }
    setBusy(true); setError("");
    try {
      await contractApi(context, catalog.admin ? "guardar_rol" : "guardar_alcances", {
        user_id: activeUser.user_id, area_id: areaId,
        ...(catalog.admin ? { rol: role } : {}),
        alcances: role === "coordinador" ? [] : scopes,
      });
      await finish("Asignación actualizada.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };
  const removeAssignment = async () => {
    if (!catalog.admin || !activeUser || !areaId || busy) return;
    if (!window.confirm("¿Quitar esta área? Se eliminarán definitivamente todas las filas de asignación del usuario a esta área, incluidos frentes y subfrentes. Las otras áreas y contratos no se modificarán.")) return;
    setBusy(true); setError("");
    try {
      const result = await contractApi<{ ok: boolean; filas_eliminadas: number }>(
        context,"quitar_usuario_area",{ user_id: activeUser.user_id, area_id: areaId },
      );
      if (!result.ok || !Number.isInteger(result.filas_eliminadas) || result.filas_eliminadas < 1) {
        throw new Error("No se pudo confirmar la eliminación de las asignaciones en la base de datos.");
      }
      await finish("Asignación al área eliminada de la base de datos.");
      setModal(null);
      setAreaId("");
      setRole("supervisor");
      setScopes([]);
    } catch(caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };
  const openProfile = async (user: ContractUser) => {
    if (!catalog.admin) return;
    setSelected(user);
    setProfile(emptyProfile);
    setModal("profile");
    setError("");
    setProfileLoading(true);
    try {
      setProfile(await usersAdminApi<PersonalProfile>(context,"consultar_perfil",{user_id:user.user_id}));
    } catch(caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setProfileLoading(false); }
  };
  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !catalog.admin || busy) return;
    setBusy(true); setError("");
    try {
      await usersAdminApi(context,"guardar_perfil",{user_id:selected.user_id,...profile});
      await finish("Datos personales actualizados.");
      setModal(null);
    } catch(caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };
  const openDelete = (user: ContractUser) => {
    setSelected(user); setConfirmDelete(""); setError(""); setModal("delete");
  };
  const deleteUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !catalog.admin || confirmDelete !== "ELIMINAR" || busy) return;
    setBusy(true); setError("");
    try {
      await deleteDatabaseUser(context,selected.user_id);
      await finish("Usuario eliminado de la base de datos.");
      setModal(null);
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };
  const toggleActive = async (user: ContractUser) => {
    if (busy) return;
    setBusy(true);setError("");
    try {
      await contractApi(context,"set_activo",{
        user_id: user.user_id, activo: !user.asignaciones.some(a=>a.is_active),
      });
      await finish("Estatus del usuario actualizado.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setBusy(false); }
  };

  const scopeEditor = <div className="ca-scopes">
    <p>Selecciona uno o varios frentes. “Todo el frente” incluye sus subfrentes.</p>
    {catalog.frentes.filter(f=>f.is_active!==false).map(f=>{
      const whole = scopes.some(s=>s.frente_id===f.id && s.subfrente_id===null);
      const subs = catalog.subfrentes.filter(s=>s.frente_id===f.id && s.is_active!==false);
      return <div className="ca-front" key={f.id}>
        <label className="ca-check"><input type="checkbox" checked={whole} disabled={!canEditSelected || busy}
          onChange={e=>setScopes(previous=>e.target.checked
            ? [...previous.filter(s=>s.frente_id!==f.id),{frente_id:f.id,subfrente_id:null}]
            : previous.filter(s=>s.frente_id!==f.id))}/><strong>{f.frente} — Todo el frente</strong></label>
        {!whole && subs.map(s=><label key={s.id} className="ca-check ca-sub">
          <input type="checkbox" disabled={!canEditSelected || busy}
            checked={scopes.some(scope=>scope.subfrente_id===s.id)}
            onChange={e=>setScopes(previous=>e.target.checked
              ? [...previous,{frente_id:f.id,subfrente_id:s.id}]
              : previous.filter(item=>item.subfrente_id!==s.id))}/>
          {s.subfrente}
        </label>)}
      </div>;
    })}
    {!catalog.frentes.length && <p>Primero registra los frentes del contrato.</p>}
  </div>;

  const assignedAreaIds = [...new Set(activeUser?.asignaciones.map(a=>a.area_id).filter((id):id is string=>Boolean(id)) ?? [])];
  const availableAreas = assignableAreas.filter(a=>
    catalog.admin || assignedAreaIds.includes(a.id));
  const summary = (item: ContractUser, id: string | null) => {
    const list=item.asignaciones.filter(a=>a.area_id===id);
    const active=list.filter(a=>a.is_active);
    if (!active.length) return "Inactivo";
    if (list[0]?.rol==="coordinador" || list[0]?.rol==="admin") return "Todo el contrato";
    return active.map(a=>a.frente_id
      ? (catalog.frentes.find(f=>f.id===a.frente_id)?.frente || "Frente")
        + (a.subfrente_id ? " / "+(catalog.subfrentes.find(s=>s.id===a.subfrente_id)?.subfrente || "Subfrente") : "")
      : "Todo el contrato").join(", ");
  };

  return <section className="panel ca-users-panel">
    <div className="toolbar"><strong>Usuarios del contrato ({catalog.usuarios.length})</strong>
      {catalog.admin && <button className="button primary" onClick={openAssign}><Plus size={16}/>Asignar usuario</button>}
    </div>
    {notice && <div className="notice success ca-alert">{notice}</div>}
    {error && !modal && <div className="ca-alert"><ErrorNotice message={error}/></div>}
    {!catalog.usuarios.length ? <Empty message="No hay usuarios asignados en el alcance de consulta."/> :
    <div className="ca-user-list">{catalog.usuarios.map(user=>{
      const adminAccount=user.asignaciones.some(a=>a.rol==="admin");
      const enabled=user.asignaciones.some(a=>a.is_active);
      const manageable=user.asignaciones.some(a=>a.area_id && canManageArea(a.area_id)
        && (catalog.admin || (a.rol!=="coordinador" && a.rol!=="admin")));
      return <div key={user.user_id} className="ca-user-card">
        <div className="ca-user-head">
          <div><strong>{user.nombre}</strong><small>{user.correo || "Sin correo"}</small>
            {user.invitacion_pendiente && <small>Invitación pendiente de asignación</small>}</div>
          <div className="ca-actions">
            {catalog.admin && !adminAccount && user.asignaciones.length>0 &&
              <button className="button secondary small" disabled={busy}
                onClick={()=>void toggleActive(user)}>{enabled?"Desactivar":"Activar"}</button>}
            {manageable || (catalog.admin && !adminAccount) ?
              <button className="button secondary small" onClick={()=>openManage(user)}>
                <UserRoundCog size={15}/>Asignaciones
              </button> : null}
            {catalog.admin && <button className="button ghost small" onClick={()=>void openProfile(user)}>
              <Pencil size={15}/>Editar datos
            </button>}
            {catalog.admin && !adminAccount && user.user_id!==currentUserId &&
              <button className="button small ca-danger" onClick={()=>openDelete(user)}>
                <Trash2 size={15}/>Eliminar cuenta
              </button>}
          </div>
        </div>
        {user.asignaciones.length>0 && <div className="ca-assignments">
          {[...new Set(user.asignaciones.map(a=>a.area_id))].map(id=>{
            const entry=user.asignaciones.find(a=>a.area_id===id);
            return <div className="ca-assignment" key={id||"general"}>
              <div><strong>{catalog.areas.find(a=>a.id===id)?.area || "Administración general"}</strong>
                <span>{roleNames[entry?.rol||""]||entry?.rol||"—"} · {summary(user,id)}</span></div>
            </div>;
          })}
        </div>}
      </div>;
    })}</div>}

    {modal==="assign" && <Modal title="Asignar usuario" onClose={close} className="ca-modal">
      <div className="ca-form">
        <div className="ca-mode-selector">
          <button type="button" className={assignMode==="existing"?"active":""}
            onClick={()=>{setAssignMode("existing");setError("");}}>Usuario registrado</button>
          <button type="button" className={assignMode==="invite"?"active":""}
            onClick={()=>{setAssignMode("invite");setError("");}}>Invitar usuario nuevo</button>
        </div>
        {assignMode==="existing" ? <>
          <label>Usuarios disponibles
            <div className="ca-search"><input placeholder="Filtrar por nombre o correo (opcional)"
              value={filter} onChange={e=>setFilter(e.target.value)} />
              <button type="button" className="button secondary" disabled={loadingAvailable}
                onClick={()=>void loadAvailable(0,filter)}>Filtrar</button></div>
          </label>
          <div className="ca-picker" role="listbox" aria-label="Usuarios registrados">
            {loadingAvailable ? <p>Cargando usuarios…</p> :
              available.usuarios.length ? available.usuarios.map(u=><button key={u.user_id}
                type="button" className="ca-picker-row" role="option" aria-selected={false}
                onClick={()=>openManage(u)}><strong>{u.nombre}</strong><small>{u.correo || "Sin correo"}</small></button>)
                : <p>No hay usuarios disponibles.</p>}
          </div>
          <div className="ca-pager"><span>{available.total} usuarios disponibles</span>
            <button type="button" className="button secondary small" disabled={loadingAvailable || page===0}
              onClick={()=>void loadAvailable(page-1,filter)}>Anterior</button>
            <span>Página {page+1}</span>
            <button type="button" className="button secondary small"
              disabled={loadingAvailable || (page+1)*available.tamano>=available.total}
              onClick={()=>void loadAvailable(page+1,filter)}>Siguiente</button>
          </div>
        </> : <form onSubmit={e=>void handleInvite(e)} className="ca-form ca-inner-form">
          <label>Nombre completo<input required maxLength={150} value={inviteName}
            onChange={e=>setInviteName(e.target.value)} /></label>
          <label>Correo electrónico<input required type="email" value={inviteEmail}
            onChange={e=>setInviteEmail(e.target.value)} /></label>
          <p>Se enviará una invitación para establecer una contraseña. Después podrás asignarle área, rol y frentes.</p>
          <button type="submit" className="button primary" disabled={busy}>{busy?"Enviando…":"Enviar invitación"}</button>
        </form>}
        {error && <ErrorNotice message={error}/>}
        <div className="ca-form-footer"><button type="button" className="button secondary" onClick={close}>Cerrar</button></div>
      </div>
    </Modal>}

    {modal==="manage" && activeUser && <Modal title="Gestionar asignaciones" onClose={close} className="ca-modal">
      <div className="ca-form">
        <p className="ca-modal-subtitle"><strong>{activeUser.nombre}</strong> · {activeUser.correo||"Sin correo"}</p>
        {availableAreas.length ? <>
          <label>Área operativa<select value={areaId}
            onChange={e=>readArea(activeUser,e.target.value)}>
            <option value="">Seleccionar área</option>
            {availableAreas.map(a=><option key={a.id} value={a.id}>{a.area}
              {activeUser.asignaciones.some(item=>item.area_id===a.id)?" (asignada)":" (nueva)"}
            </option>)}
          </select></label>
          {areaId && <div className="ca-area-editor">
            <label>Rol en el área
              <select value={role} disabled={!catalog.admin || !canEditSelected || busy}
                onChange={e=>{setRole(e.target.value);setScopes([]);}}>
                {["coordinador","supervisor","auxiliar","lector"].map(r=>
                  <option value={r} key={r}>{roleNames[r]}</option>)}
              </select>
            </label>
            {existingEntry && !isActiveInArea && <div className="notice info">
              El usuario está inactivo en esta área. Actívalo desde su tarjeta antes de modificar sus asignaciones.
            </div>}
            {role!=="coordinador" && entries.some(entry=>entry.is_active && entry.frente_id===null) &&
              <div className="notice info">Este usuario conserva una asignación general existente. Para modificarla, selecciona los frentes o subfrentes correspondientes.</div>}
            {role==="coordinador" ?
              <div className="notice info">El coordinador tiene alcance a todo el contrato exclusivamente para esta área.</div>
              : scopeEditor}
            <div className="ca-form-footer">
              {catalog.admin && existingEntry && existingEntry.rol!=="admin" &&
                <button type="button" className="button ca-danger" disabled={busy}
                  onClick={()=>void removeAssignment()}><Trash2 size={15}/>Quitar área</button>}
              <button type="button" className="button primary" disabled={!canEditSelected || busy ||
                  (role!=="coordinador" && scopes.length===0)}
                onClick={()=>void saveAssignment()}>{busy?"Guardando…":"Guardar asignación"}</button>
            </div>
          </div>}
        </> : <p>Sin áreas disponibles para modificar. Solicita al administrador asignar un área.</p>}
        {error && <ErrorNotice message={error}/>}
        <div className="ca-form-footer"><button className="button secondary" type="button" onClick={close}>Cerrar</button></div>
      </div>
    </Modal>}

    {modal==="profile" && selected && <Modal title="Editar datos personales" onClose={close} className="ca-modal">
      <form className="ca-form" onSubmit={e=>void saveProfile(e)}>
        <p className="ca-modal-subtitle">{selected.correo || selected.nombre}</p>
        {profileLoading ? <p>Cargando perfil…</p> :
          <div className="ca-profile-grid">
            <label>Nombre<input required maxLength={120} value={profile.nombre}
              onChange={e=>setProfile(p=>({...p,nombre:e.target.value}))}/></label>
            <label>Apellido paterno<input maxLength={120} value={profile.apellido_paterno}
              onChange={e=>setProfile(p=>({...p,apellido_paterno:e.target.value}))}/></label>
            <label>Apellido materno<input maxLength={120} value={profile.apellido_materno}
              onChange={e=>setProfile(p=>({...p,apellido_materno:e.target.value}))}/></label>
            <label>Teléfono<input type="tel" maxLength={40} value={profile.telefono}
              onChange={e=>setProfile(p=>({...p,telefono:e.target.value}))}/></label>
            <label className="ca-full">Notas<textarea rows={4} maxLength={3000}
              value={profile.notas} onChange={e=>setProfile(p=>({...p,notas:e.target.value}))}/></label>
          </div>}
        {error && <ErrorNotice message={error}/>}
        <div className="ca-form-footer"><button type="button" className="button secondary" onClick={close}>Cancelar</button>
          <button type="submit" className="button primary" disabled={busy || profileLoading}>{busy?"Guardando…":"Guardar"}</button></div>
      </form>
    </Modal>}

    {modal==="delete" && selected && <Modal title="Eliminar usuario definitivamente" onClose={close} className="ca-modal">
      <form className="ca-form" onSubmit={e=>void deleteUser(e)}>
        <div className="ca-delete-panel">
          <div className="notice error"><Trash2 size={18}/>Esta acción elimina el usuario de la base de datos, su perfil y todas las asignaciones vinculadas. No se puede deshacer.</div>
          <p><strong>Usuario:</strong> {selected.nombre}<br/><strong>Correo:</strong> {selected.correo || "Sin correo"}</p>
          <p>Si pertenece a otros contratos o tiene registros históricos, la eliminación será bloqueada. En ese caso puedes desactivarlo.</p>
          <label>Escribe <strong>ELIMINAR</strong> para confirmar
            <input autoComplete="off" value={confirmDelete} onChange={e=>setConfirmDelete(e.target.value)} /></label>
        </div>
        {error && <ErrorNotice message={error}/>}
        <div className="ca-form-footer"><button type="button" className="button secondary" onClick={close}>Cancelar</button>
          <button type="submit" className="button ca-danger" disabled={busy || confirmDelete!=="ELIMINAR"}>
            {busy?"Procesando…":"Eliminar definitivamente"}
          </button></div>
      </form>
    </Modal>}
  </section>;
}
